"use client";
import { useEffect, useRef, useState } from "react";
import type { RangeKeyDict } from "react-date-range";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { CalenderIcon } from "../../icons";
import Label from "./Label";

// Props for the DateRangePicker
type PropsType = {
  id: string;
  onChange?: (dates: Date[], dateStr: string) => void;
  defaultDate?: { startDate: Date; endDate: Date };
  label?: string;
  placeholder?: string;
};

export default function DateRangePicker({
  id,
  onChange,
  label,
  defaultDate,
  placeholder,
}: PropsType) {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState<DateRange>({
    startDate: defaultDate?.startDate || new Date(),
    endDate: defaultDate?.endDate || new Date(),
    key: "selection",
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close picker on outside click and invalidate if incomplete
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        // If incomplete (only one date), clear
        if (!range.startDate || !range.endDate || range.startDate.getTime() === range.endDate.getTime()) {
          setRange({ startDate: undefined, endDate: undefined, key: "selection" });
          if (onChange) onChange([], "");
        }
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, range, onChange]);

  const handleRangeChange = (ranges: RangeKeyDict) => {
    const sel = ranges.selection;
    setRange(sel);
    if (onChange) {
      const dates = [sel.startDate, sel.endDate].filter((date): date is Date => date !== undefined);
      const dateStr = dates.map(d => d.toLocaleDateString()).join(" - ");
      onChange(dates, dateStr);
    }
    // Auto-close when both dates are selected and they are not the same day
    if (sel.startDate && sel.endDate && sel.startDate.getTime() !== sel.endDate.getTime()) {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      {label && <Label htmlFor={id}>{label}</Label>}
      <div className="relative">
        <input
          id={id}
          ref={inputRef}
          readOnly
          value={
            range.startDate && range.endDate && range.startDate.getTime() !== range.endDate.getTime()
              ? `${range.startDate.toLocaleDateString()} - ${range.endDate.toLocaleDateString()}`
              : ""
          }
          placeholder={placeholder || "Select date range"}
          className="h-11 w-full rounded-lg border appearance-none px-4 pr-12 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3  dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30  bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/20 dark:border-gray-700  dark:focus:border-brand-800"
          onClick={() => setOpen((o) => !o)}
        />
        <span className="absolute text-gray-500 -translate-y-1/2 pointer-events-none right-3 top-1/2 dark:text-gray-400">
          <CalenderIcon className="size-6" />
        </span>
        {open && (
          <div ref={popoverRef} className="absolute z-50 mt-2 bg-white rounded shadow-lg right-0">
            <DateRange
              ranges={[range]}
              onChange={handleRangeChange}
              moveRangeOnFirstSelection={false}
              rangeColors={["#2563eb"]}
              showMonthAndYearPickers={true}
              editableDateInputs={true}
              months={2}
              direction="horizontal"
              className="border rounded"
            />
            <button
              className="mt-2 px-3 py-1 border rounded bg-blue-600 text-white"
              onClick={() => {
                // If incomplete (only one date), clear
                if (!range.startDate || !range.endDate || range.startDate.getTime() === range.endDate.getTime()) {
                  setRange({ startDate: undefined, endDate: undefined, key: "selection" });
                  if (onChange) onChange([], "");
                }
                setOpen(false);
              }}
              type="button"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
} 